import {
    AvailableStatisticsFieldsArray,
    StatisticsConfiguration,
    StatProps
} from '../../ConfigureReport/FieldsTab/Types'
import { convertDurationAsFormattedStringToSeconds, convertDurationInSecondsToFormattedString } from './TimeFormats'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE,
    iREPORT_VALUE_ALARM_SETTINGS_VALUE_NONE,
    iREPORT_VALUE_ALARM_SOUND_TYPE_NONE
} from '../../../NonReact/Alarms/AlarmConstants'
import {
    STATISTICS_CUSTOM_WIDTH_DEFAULT,
    STATISTICS_ORDERING_NONE,
    STATISTICS_UNIT_TYPE_CURRENCY,
    STATISTICS_UNIT_TYPE_DURATION,
    STATISTICS_UNIT_TYPE_FLOATORDOUBLE,
    STATISTICS_UNIT_TYPE_INTEGERORLONG,
    STATISTICS_UNIT_TYPE_PERCENTAGE,
    STATISTICS_UNIT_TYPE_TEXT,
    STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE
} from '../../ConfigureReport/FieldsTab/Constants'

export const STATISTICS_SETTINGS_FORMATTAG_VALUE_DELIMITER = '"'
export const STATISTICS_SETTINGS_LIST_SEPARATOR = ','

const iREPORT_STAT_ALARM_TYPE_UNSUPPORTED = 0 // Statistic doesn't support individual alarms.

/**
 * Utility method for mapping StatInfo + CStatSetting objects into StatisticsConfiguration object.
 *
 * @param statInfoObject Statistics field definition object.
 * @param cStatSettings CStatSettings object that contains configuration values for this statistics configuration.
 * @param percentageFormatString Format string for percentage related fields.
 * @param language Currently active language with loaded "TimeFormats" language resource.
 * @param currencyFormatter Currently configured currency formatter.
 */
const mapCStatSettingsToStatisticsConfiguration = (
    statInfoObject: StatProps,
    cStatSettings: CStatSettings,
    percentageFormatString: string,
    language: CLanguageHandler,
    currencyFormatter: CDecimalFormatter
): StatisticsConfiguration => {
    // Prepare formatter function for different values type e.g. currency, duration...
    let valueMapperFunction: (input: number) => string
    switch (statInfoObject.UnitType) {
        case STATISTICS_UNIT_TYPE_PERCENTAGE:
            valueMapperFunction = (input: number): string => {
                const inputValue = input ? `${input}` : `0`
                return StringExpandPlaceholders(percentageFormatString, [inputValue])
            }
            break
        case STATISTICS_UNIT_TYPE_DURATION:
            valueMapperFunction = (input: number): string => convertDurationInSecondsToFormattedString(input, language)
            break
        case STATISTICS_UNIT_TYPE_CURRENCY:
            valueMapperFunction = (input: number): string => currencyFormatter.Format(input)
            break
        case STATISTICS_UNIT_TYPE_FLOATORDOUBLE:
            valueMapperFunction = (input: number): string => (input ? String(input) : String(0.0))
            break
        case STATISTICS_UNIT_TYPE_INTEGERORLONG:
            valueMapperFunction = (input: number): string => (input ? String(input) : String(0))
            break
        case STATISTICS_UNIT_TYPE_TEXT: // Deliberate fall-through, since the removal of escaping characters is already done when creating CStatSettings object.
        case STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE: // Deliberate fall-through, since the removal of escaping characters is already done when creating CStatSettings object.
        default:
            // No mapping / conversion done.
            valueMapperFunction = (input: number): string => (input ? String(input) : '')
    }

    return {
        ID: statInfoObject.StatID,
        shortName: statInfoObject.ShortName,
        mediumName: statInfoObject.MediumName,
        unitType: statInfoObject.UnitType,
        statFlagsType: statInfoObject.StatFlagsType,
        isTitleDisplayed: cStatSettings.getIsTitleDisplayed(),
        displayedAs: cStatSettings.getDisplayedAs(),
        colourText: cStatSettings.getColourText(),
        colourBackground: cStatSettings.getColourBackground(),
        alarmType: cStatSettings.getAlarmType(),
        alarmSoundType: cStatSettings.getAlarmSoundType(),
        alarmRepeatType: cStatSettings.getAlarmRepeatType(),
        alarmSettingsValue: cStatSettings.getAlarmSettingsValue(),
        alarmComparisonType: cStatSettings.getAlarmComparisonType(),
        alarmComparisonValue1: valueMapperFunction(cStatSettings.getAlarmComparisonValue1()),
        alarmComparisonValue2: valueMapperFunction(cStatSettings.getAlarmComparisonValue2()),
        graphDialScaleMaxDefault:
            cStatSettings.getGraphDialScaleMaxDefault() !== null
                ? valueMapperFunction(parseFloat(cStatSettings.getGraphDialScaleMaxDefault()))
                : '',
        isGraphDialFacadeReversed: cStatSettings.getIsGraphDialFacadeReversed(),
        isGraphDialAxisReversed: cStatSettings.getIsGraphDialAxisReversed(),
        userContentFilePath: cStatSettings.getUserContentFilePath(),
        userContentFitTile: cStatSettings.getUserContentFitTile(),
        ordering: cStatSettings.getOrdering(),
        customName: cStatSettings.getCustomName() || '', // It is possible that null is returned, in that case, fill in empty string.
        customWidth: cStatSettings.getCustomWidth()
    }
}

/**
 * Mapping method for mapping statisticsConfiguration string (as received from server-side) to StatisticConfiguration type (as used within this app).
 *
 * @param statisticsDisplay Report / report definition property.
 * @param statisticsSettings Report / report definition property.
 * @param statsList List statistics fields currently available for this report.
 * @param percentageFormatString Format string for percentage related fields.
 * @param language Currently active language with loaded "TimeFormats" language resource.
 * @param currencyFormatter Currently configured currency formatter.
 */
export const mapStatisticsDisplayAndStatisticsStringToStatsConfigs = (
    statisticsDisplay: string,
    statisticsSettings: string,
    statsList: AvailableStatisticsFieldsArray,
    percentageFormatString: string,
    language: CLanguageHandler,
    currencyFormatter: CDecimalFormatter
): StatisticsConfiguration[] => {
    // Prepare statisticsConfigurations list
    let statisticConfigurations: StatisticsConfiguration[] = []

    // Split display statistic value into a list of separate strings between each separator.
    const pStatItems = statisticsDisplay?.split(STATISTICS_SETTINGS_LIST_SEPARATOR)
    // Split statistic settings value into a list of separate strings between each separator.
    const pStatSettingItems = statisticsSettings?.SplitEx(
        STATISTICS_SETTINGS_LIST_SEPARATOR,
        STATISTICS_SETTINGS_FORMATTAG_VALUE_DELIMITER
    )

    pStatSettingItems?.forEach((statSettingItemString, index) => {
        const statInfoID = parseInt(pStatItems[index], 10)
        const statInfoObject = statsList?.find((statInfoObj) => statInfoObj.StatID === statInfoID)

        if (statInfoObject) {
            const iAlarmType = statInfoObject?.AlarmType

            // We should only read alarm settings if supported by the statistic.
            const bReadAlarmSettings = iAlarmType !== iREPORT_STAT_ALARM_TYPE_UNSUPPORTED

            // eslint-disable-next-line
            const statSettings: CStatSettings = com.Akixi.RptUtils.StatSettingsCreateFromTextStr(
                iAlarmType,
                statSettingItemString,
                true,
                true,
                bReadAlarmSettings,
                true
            )

            statisticConfigurations.push(
                mapCStatSettingsToStatisticsConfiguration(
                    statInfoObject,
                    statSettings,
                    percentageFormatString,
                    language,
                    currencyFormatter
                )
            )
        }
    })

    // Remove duplicates if any present.
    statisticConfigurations = statisticConfigurations.filter(
        (statisticsConfiguration, index, statisticConfigurationsArray) =>
            statisticConfigurationsArray.findIndex(
                (statisticsConfigurationItema) => statisticsConfigurationItema.ID === statisticsConfiguration.ID
            ) === index
    )

    return statisticConfigurations
}

/**
 * Mapping method for mapping statistic field definition to statistics configuration.
 *
 * @param statProp Statistics field definition.
 * @param percentageFormatString Format string for percentage related fields.
 * @param language Currently active language with loaded "TimeFormats" language resource.
 * @param currencyFormatter Currently configured currency formatter.
 */
export const mapStatPropToStatisticsConfiguration = (
    statProp: StatProps,
    percentageFormatString: string,
    language: CLanguageHandler,
    currencyFormatter: CDecimalFormatter
): StatisticsConfiguration => {
    // Prepare formatter function for different values type e.g. currency, duration...
    let numericValueMapperFunction: (input: string) => string
    switch (statProp.UnitType) {
        case STATISTICS_UNIT_TYPE_PERCENTAGE:
            numericValueMapperFunction = (input: string): string => {
                const inputValue = input ? `${input}` : `0`
                return StringExpandPlaceholders(percentageFormatString, [inputValue])
            }
            break
        case STATISTICS_UNIT_TYPE_DURATION:
            numericValueMapperFunction = (input: string): string => {
                const inputValue: number = parseInt(input, 10) ? parseInt(input, 10) : 0
                return convertDurationInSecondsToFormattedString(inputValue, language)
            }
            break
        case STATISTICS_UNIT_TYPE_CURRENCY:
            // We assume this is a float number stripped from formatting characters and normalized to use full-stop character as decimal separator.
            numericValueMapperFunction = (input: string): string => {
                const inputValue: number = parseFloat(input) ? parseFloat(input) : 0
                return currencyFormatter.Format(inputValue)
            }
            break
        case STATISTICS_UNIT_TYPE_FLOATORDOUBLE:
            numericValueMapperFunction = (input: string): string => input || String(0.0)
            break
        case STATISTICS_UNIT_TYPE_INTEGERORLONG:
            numericValueMapperFunction = (input: string): string => input || String(0)
            break
        case STATISTICS_UNIT_TYPE_TEXT:
        default:
            // no mapping function
            numericValueMapperFunction = (input: string): string => (input ? String(input) : '')
    }

    return {
        ID: statProp.StatID,
        shortName: statProp.ShortName,
        mediumName: statProp.MediumName,
        unitType: statProp.UnitType,
        statFlagsType: statProp.StatFlagsType,
        isTitleDisplayed: statProp.IsTitleDisplayed,
        displayedAs: statProp.DisplayedAs,
        colourText: statProp.ColourTxt,
        colourBackground: statProp.ColourBG,
        alarmType: statProp.AlarmType,
        alarmSoundType: iREPORT_VALUE_ALARM_SOUND_TYPE_NONE, // StatInfo API currently not does not return (support) this parameter.
        alarmRepeatType: statProp.AlarmRepeatType || iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE,
        alarmSettingsValue: iREPORT_VALUE_ALARM_SETTINGS_VALUE_NONE, // statProp.Alarm // mising in StatInfo type,
        alarmComparisonType: statProp.AlarmComparisonType,
        alarmComparisonValue1: numericValueMapperFunction(statProp.AlarmComparisonValue1),
        alarmComparisonValue2: numericValueMapperFunction(statProp.AlarmComparisonValue2),
        graphDialScaleMaxDefault: '', // This is null in previous implementation
        isGraphDialFacadeReversed: statProp.IsGraphDialFacadeReversed,
        isGraphDialAxisReversed: statProp.IsGraphDialAxisReversed,
        userContentFilePath: statProp.UserContentFilePath || '',
        userContentFitTile: statProp.UserContentFitTile,
        ordering: STATISTICS_ORDERING_NONE, // mising in StatInfo type ,
        customName: '',
        customWidth: STATISTICS_CUSTOM_WIDTH_DEFAULT
    }
}

/**
 *
 * @param configuredStatisticsConfigurations List of statistic configurations to map to request string.
 * @param currencyFormatter Currently used currency formatter used to format / parse financial values.
 */
export const mapStatisticsConfigurationsToStatisticsSettingsString = (
    configuredStatisticsConfigurations: StatisticsConfiguration[],
    currencyFormatter: CDecimalFormatter
): string =>
    configuredStatisticsConfigurations
        // Remove duplicates if any.
        .filter(
            (statisticsConfiguration, index, configuredStatisticsConfigurationsArray) =>
                configuredStatisticsConfigurationsArray.findIndex(
                    (statisticsConfigurationInFind) => statisticsConfigurationInFind.ID === statisticsConfiguration.ID
                ) === index
        )
        .map((statisticsConfiguration: StatisticsConfiguration): { getSettingsAsText: () => string } => {
            let numericValueParserMapperFunction
            switch (statisticsConfiguration.unitType) {
                case STATISTICS_UNIT_TYPE_PERCENTAGE:
                    numericValueParserMapperFunction = (input: string): string =>
                        // Remove % char
                        input ? input.replace('%', '') : input

                    break
                case STATISTICS_UNIT_TYPE_DURATION:
                    numericValueParserMapperFunction = (input: string): string =>
                        input ? String(convertDurationAsFormattedStringToSeconds(input, 0)) : input
                    break
                case STATISTICS_UNIT_TYPE_CURRENCY:
                    numericValueParserMapperFunction = (input: string): string =>
                        input ? String(currencyFormatter.Parse(input)) : input
                    break
                case STATISTICS_UNIT_TYPE_FLOATORDOUBLE:
                    // Make sure it is parsable, if not, provide default 0.0
                    numericValueParserMapperFunction = (input: string): string =>
                        input ? String(Number.parseFloat(input)) : String(0.0)
                    break
                case STATISTICS_UNIT_TYPE_INTEGERORLONG:
                    // Make sure it is parsable, if not, provide default 0
                    numericValueParserMapperFunction = (input: string): string =>
                        input ? String(Number.parseInt(input, 10)) : String(0)
                    break
                case STATISTICS_UNIT_TYPE_TEXT:
                case STATISTICS_UNIT_TYPE_TEXT_TRUNCATABLE:
                    numericValueParserMapperFunction = (input: string): string => input
                    break
                default:
                    numericValueParserMapperFunction = (input: string): string => input
            }

            let statisticCustomWidth = statisticsConfiguration.customWidth
            // The width has to be between 0 and 100, but if user configured -1, we keep it since -1 means default.
            if (statisticCustomWidth < -1) {
                statisticCustomWidth = 0
            } else if (statisticCustomWidth > 100) {
                statisticCustomWidth = 100
            }

            // Maximum number of character for custom name is 100.
            let customName = statisticsConfiguration.customName
            if (customName && customName.length > 100) {
                customName = customName.slice(0, 100)
            }

            // eslint-disable-next-line
            return new com.Akixi.RptUtils.CStatSettings(
                statisticsConfiguration.isTitleDisplayed,
                statisticsConfiguration.displayedAs,
                statisticsConfiguration.colourText,
                statisticsConfiguration.colourBackground,
                statisticsConfiguration.alarmType,
                statisticsConfiguration.alarmComparisonType,
                numericValueParserMapperFunction(statisticsConfiguration.alarmComparisonValue1),
                numericValueParserMapperFunction(statisticsConfiguration.alarmComparisonValue2),
                numericValueParserMapperFunction(statisticsConfiguration.graphDialScaleMaxDefault),
                statisticsConfiguration.isGraphDialFacadeReversed,
                statisticsConfiguration.isGraphDialAxisReversed,
                statisticsConfiguration.userContentFilePath,
                statisticsConfiguration.userContentFitTile,
                statisticsConfiguration.ordering,
                statisticsConfiguration.alarmSoundType,
                statisticsConfiguration.alarmRepeatType,
                statisticsConfiguration.alarmSettingsValue,
                customName,
                statisticCustomWidth
            ) as { getSettingsAsText: () => string }
        })
        .map((statSettings: { getSettingsAsText: () => string }): string => statSettings.getSettingsAsText())
        .join(STATISTICS_SETTINGS_LIST_SEPARATOR)
